'use strict';
import {
  Component,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
  AfterViewInit,
  ChangeDetectorRef,
} from '@angular/core';
import { ResultsStoreService } from '@services/results/results-store.service';
import { ApplicationStatus } from '@services/results/application-status';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { RouterOutlet } from '@angular/router';
import { fromEvent, Subject, BehaviorSubject } from 'rxjs';
import { slideInAnimation } from './route-animation';
import { MenuItem } from '@models/menu-item';
import { MenuService } from '@services/menu/menu.service';
import { TableDataService } from '@services/lookup-tables/table-data.service';
import { SimulationSetService } from '@services/simulation-set/simulation-set.service';
import { delay, takeUntil, filter, distinctUntilChanged } from 'rxjs/operators';
import { isEscape, isCtrl, isAlt, isShift, isCtrlC, isAltLeftArrow } from '@helpers/key-codes';
import { OpenIdConnectService } from '@services/auth/open-id-connect.service';
import { isLiquidityReadOnly } from '@models/user-level';

@Component({
  selector: 'app-dialog',
  exportAs: 'dialog',
  templateUrl: './dialog.component.html',
  styleUrls: ['./dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    slideInAnimation,
    // animation triggers go here
  ],
})
export class DialogComponent implements OnInit, OnDestroy, AfterViewInit {
  private destroyed$ = new Subject();
  @Output() modalClose: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild(RouterOutlet, { static: false }) public routerOutlet?: RouterOutlet;

  public eventReactor$ = new BehaviorSubject(null);
  public isRightClickSwalOpen = false;
  public previousActionIsCopy = false;
  public isImported = false;
  // private subscriptions: Subscription[] = [];
  public hideSaveButton = false;
  public maximized = false;
  public tabs: MenuItem[] = [];

  setIsImported(value: boolean) {
    this.isImported = value;
  }

  get selectedItem() {
    return this.menu.selectedMenuItem;
  }

  get currentPage() {
    return this.selectedItem.routerLink;
  }

  constructor(
    public menu: MenuService,
    public dialogRef: MatDialogRef<DialogComponent>,
    public tableDataService: TableDataService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private simService: SimulationSetService,
    private changeRef: ChangeDetectorRef,
    private store: ResultsStoreService,
    private auth: OpenIdConnectService
  ) {
    this.menu.setDialogRef(this.dialogRef);

    // Track copy status for defect 4620 on Esc keypress
    this.initListeners();

    // TODO: Wrap all the event listeners inside this.initListeners()
    // Esc Key handling with Swal dialog open over the main dialog
    fromEvent(document, 'isRightClickSwalOpen')
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => (this.isRightClickSwalOpen = true)),
      fromEvent(document, 'isRightClickSwalClosed')
        .pipe(takeUntil(this.destroyed$), delay(10))
        .subscribe(() => (this.isRightClickSwalOpen = false)),
      // Handling click outside dialog
      dialogRef
        .backdropClick()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(e => {
          e.preventDefault();
          this.close(e);
        }),
      // Handle maximization
      this.menu.maximized$.pipe(takeUntil(this.destroyed$)).subscribe(value => {
        if (this.maximized !== value) {
          this.maximized = value;
          //this.toasterConfig.positionClass = this.maximized ? 'toast-top-right toast-maximized' : 'toast-top-right';
        }
      });
  }

  ngOnInit() {
    this.tabs = this.menu.getMenu(this.data.type);

    // Bug fix 2965 - Part 2 - Reset simulationSetState when tab is opened (but not when the user is switching between tabs)
    if (this.menu.selectedMenuItem.outlet === 'simulation-outlet') {
      this.simService.resetSimulationState();
    }
  }

  public ngAfterViewInit() {
    this.changeRef.detectChanges();
  }

  // On Dialog Close:
  public ngOnDestroy() {
    // Emit to unsubscribe subscriptions to avoid memory leaks
    this.destroyed$.next();
    this.destroyed$.complete();

    if (this.routerOutlet.isActivated) {
      this.routerOutlet.deactivate();
    }

    // this.menu.selectedMenuItem = null;
  }

  // TODO: Incorporate all the listeners inside this.initListeners()
  private initListeners() {
    // Reactor
    this.initReactor();
    // Keyboard events
    this.initKeyboardEventsListener();
  }

  private initKeyboardEventsListener() {
    // TODO: it does not triggers on delete or backspace key press. We can add them using a forkJoin
    this.dialogRef
      .keydownEvents()
      .pipe(
        takeUntil(this.destroyed$),
        distinctUntilChanged(),
        // ignore alt, shiftKey, ctrl key presses as is the first key for a command combination
        filter(ke => !(isCtrl(ke) || isAlt(ke) || isShift(ke)))
      )
      .subscribe((ke: KeyboardEvent) => {
        if (isEscape(ke)) {
          // Prevent closing dialog if Escape key was pressed on Equation Builder window, any Spread dropdown
          // or has performs the action of copying a cell and press Escape after that
          const path = event['path'].map(x => x.tagName);
          if (
            this.store.appStatus$.value !== ApplicationStatus.ScenariosModified &&
            !path.includes('KENDO-WINDOW') &&
            !(path.includes('GC-SPREAD-SHEETS') && path.includes('SELECT')) &&
            // Prevent closing dialog when previous action is Ctrl+C / copy and the user has press Escape - Bug 4620
            !this.previousActionIsCopy
          ) {
            // event.preventDefault();
            this.close(event);
          }
          // Prevent closing dialog when pressing Alt+Left - Bug 3201
        } else if (isAltLeftArrow(ke)) event.preventDefault();

        // Fix for bug 4620
        if (isCtrlC(ke) != this.previousActionIsCopy) {
          this.eventReactor$.next({ type: 'previousActionIsCopy', value: isCtrlC(ke) });
        }
      });
  }

  private initReactor() {
    this.eventReactor$
      .pipe(
        takeUntil(this.destroyed$),
        // skip null values
        filter(param => param)
      )
      .subscribe(obj => {
        if (obj.type === 'previousActionIsCopy' && typeof obj.value === 'boolean') {
          this.previousActionIsCopy = obj.value;
        }
      });
  }

  prepareRoute(outlet: RouterOutlet) {
    return outlet && outlet.activatedRouteData && outlet.activatedRouteData['animation'];
  }

  public tabClicked(menuItem: MenuItem) {
    this.menu.selectMenuItem(menuItem).then(success => {
      if (success) {
        this.hideSaveButton = this.menu.selectedMenuItem.routerLink === 'scenarios';
      }
    });
  }

  public toggleMaximized() {
    this.menu.toggleMaximized();
  }

  public onTitlebarDoubleClick() {
    this.menu.toggleMaximized();
  }

  public close(event) {
    // If 'Escape' key was pressed on alert dialog, stop processing it for the main dialog
    if (isEscape(event) && (event.target.className.match(/swal/) || this.isRightClickSwalOpen)) {
      // console.log('If is a escape over a swal dialog displayed, it avoids closing the dialog on the bubbling');
      return;
    }

    if (this.menu.selectedMenuItemCanDeactivate()) {
      this.menu.prepareToClose();
      this.menu.closeDialog();
    } else {
      this.menu.canDeactivatePopup().then(result => {
        if (result === true) {
          this.menu.prepareToClose();
          this.menu.closeDialog();
        }
      });
    }
  }

  public onDialogSaveButtonClicked() {
    this.menu.dialogSaveButton = {
      ...this.menu.dialogSaveButton,
      clicked: true,
    };
  }

  public isSaveEnabled() {
    return this.menu.dialogSaveButton.page !== null && this.menu.dialogSaveButton.enabled;
  }

  public isReadOnly() {
    return isLiquidityReadOnly(this.auth.userLevel);
  }
  // Show only "Assumptions" tab
  // ToDo: Refactorize to show Edit Assumptions in his own dialog
  // ToDo: After that, remove all the stuff related with isTabEnabled() and menu.isEditingAssumptions
  public isTabEnabled(tab) {
    // Disable the simulation-set tab from the "Edit Assumptions" view
    const disabledTabsInAssumptionsView = ['simulation-set'];
    if (this.menu.isEditingAssumptions && disabledTabsInAssumptionsView.includes(tab.routerLink)) {
      return false;
    }
    return true;
  }

  public get isEditingAssumptions() {
    return this.menu.isEditingAssumptions;
  }

  public forceClose() {
    this.dialogRef.close();
  }

  public openUnusedAccountsDialog() {
    this.menu.unusedAccountsWindowOpen$.next(true);
  }
}
